import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, FormEvent, ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { ArrowLeft, ImageUp, LifeBuoy } from "lucide-react";
import type { RecoveryGrant, SecondFactorOutcome } from "../../api/auth.js";
import { ApiClientError } from "../../api/http.js";
import { beginRecovery, describeAuthError, finishRecovery, recoveryKeyProblem } from "../../app/authFlow.js";
import { paths } from "../../app/paths.js";
import { useSession } from "../../app/session.js";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { CryptoClient } from "../../cryptoClient.js";
import { isValidEmail, masterPasswordProblem } from "../../lib/masterPassword.js";
import { useMinPasswordLength } from "../../data/account.js";
import { QrError, decodeQrImage } from "../../lib/qr.js";
import { Logo } from "../../ui/Logo.js";
import { Field, TextArea, TextInput } from "../../ui/forms.js";
import { Alert, Button } from "../../ui/primitives.js";
import CryptoWorker from "../../workers/crypto.worker?worker";
import { NewPasswordFields } from "../auth/NewPasswordFields.js";
import { SecondFactorStep } from "../auth/SecondFactorStep.js";
import { EmergencyKitView } from "./EmergencyKitView.js";

type Stage =
  | { readonly kind: "key" }
  | { readonly kind: "second-factor"; readonly ticket: string }
  | { readonly kind: "password"; readonly grant: RecoveryGrant }
  | { readonly kind: "kit"; readonly recoveryKey: string; readonly createdAt: number };

/**
 * Forgotten master password: recovery key from the emergency kit, second factor if the account
 * has one, new master password, new kit. All key work happens in this browser's crypto worker.
 */
export function RecoveryPage(): ReactNode {
  useDocumentTitle("Zugang wiederherstellen");
  const { state, begin } = useSession();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: "key" });
  const [email, setEmail] = useState("");
  const [recoveryKey, setRecoveryKey] = useState("");
  const clientRef = useRef<CryptoClient | undefined>(undefined);
  const handedOver = useRef(false);

  useEffect((): (() => void) => {
    return (): void => {
      if (!handedOver.current) {
        clientRef.current?.terminate();
      }
      clientRef.current = undefined;
    };
  }, []);

  if (state.status !== "locked" && !handedOver.current) {
    return <Navigate to={paths.dashboard} replace />;
  }

  function client(): CryptoClient {
    if (clientRef.current === undefined) {
      clientRef.current = new CryptoClient(() => new CryptoWorker());
    }
    return clientRef.current;
  }

  function enterVault(): void {
    const ready = clientRef.current;
    if (ready === undefined) {
      return;
    }
    handedOver.current = true;
    setRecoveryKey("");
    begin(email.trim().toLowerCase(), ready);
    navigate(paths.dashboard, { replace: true });
  }

  return (
    <main className="flex min-h-dvh items-center justify-center px-5 py-12 sm:px-10">
      <div className="w-full max-w-[26rem]">
        <div className="mb-10">
          <Logo />
        </div>
        <div className="animate-fade-up">
          {stage.kind === "key" ? (
            <KeyStep
              email={email}
              recoveryKey={recoveryKey}
              onEmail={setEmail}
              onRecoveryKey={setRecoveryKey}
              onSubmit={async (): Promise<void> => {
                const started = await beginRecovery(client(), email.trim().toLowerCase(), recoveryKey);
                setStage(started.kind === "second-factor" ? { kind: "second-factor", ticket: started.ticket } : { kind: "password", grant: started.grant });
              }}
            />
          ) : stage.kind === "second-factor" ? (
            <div className="flex flex-col gap-5">
              <SecondFactorStep
                ticket={stage.ticket}
                onVerified={async (outcome: SecondFactorOutcome): Promise<void> => {
                  if (outcome.kind !== "recovery") {
                    throw new Error("Unexpected sign-in.");
                  }
                  setStage({ kind: "password", grant: outcome.grant });
                }}
              />
              <Button variant="ghost" className="w-full" onClick={(): void => setStage({ kind: "key" })}>
                Abbrechen
              </Button>
            </div>
          ) : stage.kind === "password" ? (
            <PasswordStep
              email={email.trim().toLowerCase()}
              onSubmit={async (password: string): Promise<void> => {
                const next = await finishRecovery(client(), email.trim().toLowerCase(), recoveryKey, stage.grant, password);
                setRecoveryKey("");
                setStage({ kind: "kit", recoveryKey: next, createdAt: Date.now() });
              }}
              onCancel={(): void => setStage({ kind: "key" })}
            />
          ) : (
            <KitStep email={email.trim().toLowerCase()} recoveryKey={stage.recoveryKey} createdAt={stage.createdAt} onDone={enterVault} />
          )}
        </div>
      </div>
    </main>
  );
}

function StepHeading(props: { readonly title: string; readonly children: ReactNode }): ReactNode {
  return (
    <div>
      <span className="mb-4 grid size-10 place-items-center rounded-lg border border-line bg-ink-2 text-flow">
        <LifeBuoy size={18} aria-hidden="true" />
      </span>
      <h1 className="font-display text-[1.75rem] font-extrabold tracking-[-0.02em] text-foam">{props.title}</h1>
      <p className="mt-1.5 text-sm leading-relaxed text-mist">{props.children}</p>
    </div>
  );
}

function KeyStep(props: {
  readonly email: string;
  readonly recoveryKey: string;
  readonly onEmail: (value: string) => void;
  readonly onRecoveryKey: (value: string) => void;
  readonly onSubmit: () => Promise<void>;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const fileRef = useRef<HTMLInputElement | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) {
      return;
    }
    const problem = !isValidEmail(props.email.trim()) ? "Bitte gib deine E-Mail-Adresse ein." : recoveryKeyProblem(props.recoveryKey);
    setError(problem);
    if (problem !== undefined) {
      return;
    }
    setBusy(true);
    try {
      await props.onSubmit();
    } catch (err: unknown) {
      setError(err instanceof ApiClientError && err.status === 401 ? "E-Mail oder Wiederherstellungsschlüssel ist falsch." : describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function readImage(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file === undefined) {
      return;
    }
    try {
      props.onRecoveryKey(await decodeQrImage(file));
      setError(undefined);
    } catch (err: unknown) {
      setError(err instanceof QrError ? err.message : "Das Bild konnte nicht gelesen werden.");
    }
  }

  return (
    <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5" noValidate>
      <StepHeading title="Zugang wiederherstellen">
        Mit dem Wiederherstellungsschlüssel aus deinem Notfall-Kit legst du ein neues Master-Passwort fest. Deine Einträge bleiben erhalten.
      </StepHeading>
      <Field id="recover-email" label="E-Mail">
        <TextInput
          id="recover-email"
          type="email"
          autoComplete="username"
          autoFocus
          inputMode="email"
          value={props.email}
          onChange={(event): void => props.onEmail(event.target.value)}
          placeholder="name@firma.de"
          disabled={busy}
        />
      </Field>
      <Field id="recover-key" label="Wiederherstellungsschlüssel" hint="56 Zeichen aus dem Notfall-Kit. Bindestriche und Leerzeichen sind egal.">
        <TextArea
          id="recover-key"
          rows={3}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          className="font-mono tracking-wide"
          value={props.recoveryKey}
          onChange={(event): void => props.onRecoveryKey(event.target.value)}
          placeholder="ABCD-EFGH-…"
          disabled={busy}
        />
      </Field>
      <div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(event): void => void readImage(event)} />
        <Button variant="secondary" size="sm" icon={ImageUp} onClick={(): void => fileRef.current?.click()} disabled={busy}>
          QR-Code aus Bild lesen
        </Button>
      </div>
      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
      <Button type="submit" size="lg" className="w-full" loading={busy}>
        Weiter
      </Button>
      <p className="text-center text-sm">
        <Link to={paths.login} className="inline-flex items-center gap-1.5 text-mist underline-offset-4 hover:text-foam hover:underline">
          <ArrowLeft size={14} aria-hidden="true" />
          Zurück zur Anmeldung
        </Link>
      </p>
      <p className="border-t border-line pt-5 text-[0.8125rem] leading-relaxed text-mist-2">
        Kein Notfall-Kit? Dann lässt sich das Master-Passwort nicht zurücksetzen – auch nicht durch deinen Administrator.
      </p>
    </form>
  );
}

function PasswordStep(props: { readonly email: string; readonly onSubmit: (password: string) => Promise<void>; readonly onCancel: () => void }): ReactNode {
  const minLength = useMinPasswordLength();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) {
      return;
    }
    const problem = masterPasswordProblem(password, confirm, undefined, minLength) ?? (consent ? undefined : "Bitte bestätige den Hinweis.");
    setError(problem);
    if (problem !== undefined) {
      return;
    }
    setBusy(true);
    try {
      await props.onSubmit(password);
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setPassword("");
      setConfirm("");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5" noValidate>
      <StepHeading title="Neues Master-Passwort">
        Der Schlüssel passt. Lege jetzt ein neues Master-Passwort fest. Danach sind alle Geräte abgemeldet und du bekommst ein neues Notfall-Kit.
      </StepHeading>
      <p className="rounded-lg border border-line bg-ink-2 px-3.5 py-2.5 text-sm text-mist">
        Konto: <span className="font-medium text-foam">{props.email}</span>
      </p>
      <NewPasswordFields
        idPrefix="recover"
        minLength={minLength}
        password={password}
        confirm={confirm}
        consent={consent}
        busy={busy}
        onPassword={setPassword}
        onConfirm={setConfirm}
        onConsent={setConsent}
      />
      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
      {busy ? <p className="text-sm text-mist" role="status">Schlüssel werden abgeleitet …</p> : null}
      <div className="flex flex-col gap-2">
        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Master-Passwort speichern
        </Button>
        <Button variant="ghost" className="w-full" onClick={props.onCancel} disabled={busy}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

function KitStep(props: { readonly email: string; readonly recoveryKey: string; readonly createdAt: number; readonly onDone: () => void }): ReactNode {
  const [saved, setSaved] = useState(false);
  return (
    <div className="flex flex-col gap-5">
      <StepHeading title="Dein neues Notfall-Kit">
        Das Master-Passwort ist geändert. Das alte Notfall-Kit gilt nicht mehr – speichere dieses hier.
      </StepHeading>
      <EmergencyKitView email={props.email} recoveryKey={props.recoveryKey} createdAt={props.createdAt} saved={saved} onSaved={setSaved} />
      <Button size="lg" className="w-full" onClick={props.onDone} disabled={!saved}>
        Weiter zum Tresor
      </Button>
    </div>
  );
}
